<template>
  <MobileHeader :label="$t('nav.dashboard')" />
  <DashboardLayout>
    <template #main>
      <!-- Inspection history -->
      <DashboardChartPanel
        :showTitleOnMobile="true"
        :title="$t('shafts.inspection_history')"
      >
        <StackedChart
          v-if="inspectionHistory && !inspectionHistoryLoading"
          id="history"
          :height="200"
          :series="inspectionHistory"
        />
        <Loader
          v-if="inspectionHistoryLoading"
          class="chart-loader-history"
        />
      </DashboardChartPanel>

      <div class="chart-container">
        <div class="chart-selector typo-small-title hide-for-medium">
          <SwitchButton
            v-model="graph"
            :items="graphOptions"
            :modifiers="['soft', 'lighter']"
            name="graph"
          />
        </div>
        <!-- Issues by condition -->
        <DashboardChartPanel
          :class="{ 'show-for-medium': graph !== 'condition' }"
          :title="$t('operations.issues_by_condition')"
        >
          <template #panelHeaderActions>
            <DropdownLink
              v-if="earliestApprovedInspection && conditionParams"
              :items="[{ label: $t('general.csv'), path: conditionParams }]"
              :modifiers="['right-border', !conditionGraphData && 'hide']"
            />
            <DateFilter
              v-if="earliestApprovedInspection"
              id="condition"
              :class="!conditionGraphData && 'hide'"
              :earliestGraphDate="earliestApprovedInspection"
              :modifiers="['no-padding-right']"
              :showCustomDateRange="true"
              @update:model-value="filterGraph($event)"
            />
          </template>
          <DashboardChart
            v-if="conditionGraphData"
            chartStyle="condition"
            :series="conditionGraphData"
            @CSVparamUpdate="updateCSVPath"
          />
          <Loader
            v-else
            class="chart-loader-issue"
          />
        </DashboardChartPanel>
        <!-- Issues by risk / priority rating -->
        <DashboardChartPanel
          :class="{ 'show-for-medium': graph !== 'risk' }"
          :title="ratingPanelTitle"
        >
          <template #panelHeaderActions>
            <DropdownLink
              v-if="earliestApprovedInspection && ratingParams"
              :items="[{ label: $t('general.csv'), path: ratingParams }]"
              :modifiers="['right-border', !ratingGraphData && 'hide']"
            />
            <DateFilter
              v-if="earliestApprovedInspection"
              id="risk"
              :class="!ratingGraphData && 'hide'"
              :earliestGraphDate="earliestApprovedInspection"
              :modifiers="['no-padding-right']"
              :showCustomDateRange="true"
              @update:model-value="filterGraph($event)"
            />
          </template>
          <DashboardChart
            v-if="ratingGraphData"
            :chartStyle="graphType"
            :series="ratingGraphData"
            @CSVparamUpdate="updateCSVPath"
          />
          <Loader
            v-else
            class="chart-loader-issue"
          />
        </DashboardChartPanel>
      </div>
      <ReportsPanel
        :createPolicy="reportsStore.reportPolicies?.equipment_health_report_policy?.create"
        :fetchError="reportsStore.fetchReportsError"
        identifier="equipment"
        :loading="reportsStore.loading"
        :reports="reportsStore.reports"
        @delete-report="onReportItem"
      />
    </template>
    <template #sidebar>
      <DashboardInspections
        inspectionPath="equipmentInspection"
        :inspectionsEndpoint="ENDPOINTS.inspections"
      />

      <DashboardIssues
        :highRiskRegionsEndpoint="ENDPOINTS.highRiskRegions"
        regionsPathName="equipmentRegions"
      />
    </template>
  </DashboardLayout>
</template>

<script>
export default { name: 'EquipmentDashboard' };
</script>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { useChannel } from '@/functions/action_cable';

import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import useOrganisationProfile from '@/functions/use_organisation_profile';

// Stores
import { useReportsStore } from '@/stores/reports/reportsStore';

// Components
import DashboardChart from '@/components/charts/dashboard_chart.vue';
import DashboardInspections from '@/components/dashboard/dashboard_inspections.vue';
import DateFilter from '@/components/form/date_filter.vue';
import DropdownLink from '@/components/generic/download_link.vue';
import Loader from '@/components/generic/loader.vue';
import MobileHeader from '@/components/generic/mobile_header.vue';
import ReportsPanel from '@/components/dashboard/reports/reports_panel.vue';
import SwitchButton from '@/components/form/switch_button.vue';

// Icons
import DashboardChartPanel from '@/components/dashboard/dashboard_chart_panel.vue';
import DashboardIssues from '@/components/dashboard/dashboard_issues.vue';
import DashboardLayout from '@/components/dashboard/dashboard_layout.vue';
import StackedChart from '@/components/charts/stacked_chart.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  equipmentId: { type: String, required: true },
});

const ajax = useAjax();
const reportsStore = useReportsStore();

useChannel(
  {
    channel: 'Equipments::EquipmentHealthReportChannel',
    equipment_id: props.equipmentId,
  },
  {
    received() {
      reportsStore.fetchReports(ENDPOINTS.value.equipmentHealthReports);
    },
  }
);

const chartFilterDateRange = ref({});
const conditionGraphData = ref(null);
const conditionParams = ref(null);
const earliestApprovedInspection = ref('');
const graph = ref('condition');
const inspectionHistory = ref(null);
const inspectionHistoryLoading = ref(false);
const ratingChartFilterDateRange = ref({});
const ratingGraphData = ref(null);
const ratingParams = ref(null);

const { profileLoaded, showRisk } = useOrganisationProfile();

const graphType = computed(() => {
  if (!profileLoaded.value) return null;

  return showRisk.value ? 'risk' : 'priority';
});

// Derived from graphType rather than showRisk so the panel doesnt briefly show
// the wrong heading while the organisation profile is still loading.
const ratingPanelTitle = computed(() => {
  if (!graphType.value) return '';

  return graphType.value === 'risk'
    ? I18n.t('operations.issues_by_risk')
    : I18n.t('operations.issues_by_priority');
});

const graphOptions = computed(() => [
  { label: I18n.t('equipment.condition'), value: 'condition' },
  {
    label: graphType.value === 'priority' ? I18n.t('equipment.priority') : I18n.t('equipment.risk'),
    value: 'risk',
  },
]);

const BASE_URL = computed(() => `/organisations/${props.organisationId}/equipment/${props.equipmentId}`);

const ENDPOINTS = computed(() => {
  return {
    csv: `/internal${BASE_URL.value}/graph/download?type=`,
    dashboard: `${BASE_URL.value}/dashboard`,
    graph: `${BASE_URL.value}/graph`,
    highRiskRegions: `${BASE_URL.value}/dashboard/high_risk_regions`,
    inspections: `${BASE_URL.value}/dashboard/inspections`,
    equipmentHealthReportPolicies: `${BASE_URL.value}/dashboard/equipment_health_report_policies`,
    equipmentHealthReports: `${BASE_URL.value}/equipment_health_reports`,
  };
});

onMounted(load);

watch(() => props.equipmentId, load);

// The rating graph is loaded separately because it cannot be requested until the
// organisation profile is known.
watch([graphType, () => props.equipmentId], loadRatingGraphData, { immediate: true });

function loadRatingGraphData() {
  if (!graphType.value) return;

  loadGraphData(graphType.value, 'apex', ratingGraphData, ratingChartFilterDateRange.value);
}

function filterGraph({ id, from, to }) {
  if (id === 'condition') {
    chartFilterDateRange.value = { from, to };
    loadGraphData('condition', 'apex', conditionGraphData, chartFilterDateRange.value);
  } else {
    ratingChartFilterDateRange.value = { from, to };
    loadRatingGraphData();
  }
}

function load() {
  loadEquipment();
  loadGraphData('condition', 'apex', conditionGraphData, chartFilterDateRange.value);
  reportsStore.fetchReports(ENDPOINTS.value.equipmentHealthReports);
  reportsStore.fetchReportPolicies(ENDPOINTS.value.equipmentHealthReportPolicies);
  loadInspectionHistoryData();
}

async function loadGraphData(type, graph, ref, filterParams) {
  ref.value = null;
  let { ok, data } = await ajax(ENDPOINTS.value.graph, {
    query: {
      type,
      graph,
      start_date: filterParams?.from,
      end_date: filterParams?.to,
    },
  });

  if (ok) {
    ref.value = data;
  }
}

async function loadEquipment() {
  let { ok, data } = await ajax(ENDPOINTS.value.dashboard);

  if (ok) {
    earliestApprovedInspection.value = data.earliest_approved_inspection;
  }
}

async function loadInspectionHistoryData() {
  inspectionHistoryLoading.value = true;

  let { ok, data } = await ajax(ENDPOINTS.value.graph, {
    query: {
      type: 'detailed_inspection_history',
    },
  });

  if (ok) {
    inspectionHistory.value = data;
    inspectionHistoryLoading.value = false;
  }
}

async function onReportItem(id) {
  reportsStore.deleteReport(id, `${ENDPOINTS.value.equipmentHealthReports}/${id}`);
}

function updateCSVPath(data, type) {
  let endpoint = ENDPOINTS.value.csv;

  if (type === 'condition') {
    let params = data.map((item) => `&conditions[]=${item}`);
    return conditionParams.value = buildEndPoint(endpoint, type, params, startAndEndParam(chartFilterDateRange.value));
  }
  if (type === 'risk') {
    let params = data.map((item) => `&risk_rating_levels[]=${item}`);
    return ratingParams.value = buildEndPoint(endpoint, type, params, startAndEndParam(ratingChartFilterDateRange.value));
  }
  if (type === 'priority') {
    let params = data.map((item) => `&priorities[]=${item}`);
    return ratingParams.value = buildEndPoint(endpoint, type, params, startAndEndParam(ratingChartFilterDateRange.value));
  }
}

function startAndEndParam(rangeType) {
  return Object.keys(rangeType).length !== 0 ? `&start_date="${rangeType.from}&end_date="${rangeType.to}"` : '';
}

function buildEndPoint(endpoint, type, params, startEnd) {
  return `${endpoint}${type}${params.join('')}${startEnd}`;
}
</script>
<style scoped lang="scss">
@import '@/stylesheets/global';
</style>
